<template>
  <div class="mt-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Bill items</h5>
      <CButton v-if="!readonly" color="primary" size="sm" @click="$emit('add-item')">
        <CIcon name="cil-plus" /> Add item
      </CButton>
    </div>

    <!-- Desktop Table View -->
    <div class="table-responsive d-none d-md-block">
      <table class="table table-hover border">
        <thead>
          <tr>
            <th style="width: 50px">#</th>
            <th>Item</th>
            <th>Description</th>
            <th style="width: 100px">Quantity</th>
            <th style="width: 100px">Unit</th>
            <th style="width: 150px" class="text-right">Price (RM)</th>
            <th style="width: 150px" class="text-right">Total</th>
            <th v-if="!readonly" style="width: 120px">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in items" :key="item.id">
            <td>{{ index + 1 }}</td>
            <td>
              <div v-if="readonly" class="py-2">{{ productName(item) }}</div>
              <!-- Lines without a product (from a purchase order, a scanned bill or the AI assistant) keep their name -->
              <div v-else-if="!item.product && item.name" class="small font-weight-bold mb-1">{{ item.name }}</div>
              <v-select
                v-if="!readonly"
                v-model="item.product"
                :label="'name'"
                :options="productItems"
                placeholder="Select product"
                @input="handleProductSelect($event, item)"
              />
            </td>
            <td>
              <!-- Modal Mode: Truncated preview with edit button -->
              <div v-if="useDescriptionModal" class="description-container">
                <div
                  class="description-preview"
                  @click="openDescriptionModal(item)"
                  style="cursor: pointer; min-height: 36px; padding: 4px 0;"
                >
                  {{ truncateText(item.description, 50) || 'Click to add a description' }}
                </div>
                <CButton
                  size="sm"
                  color="light"
                  @click="openDescriptionModal(item)"
                  class="mt-1"
                >
                  <CIcon name="cil-pencil" size="sm" /> Edit
                </CButton>
              </div>

              <div v-else-if="readonly" class="py-2 description-preview">{{ item.description }}</div>
              <!-- Inline Mode: Direct textarea editing -->
              <CTextarea
                v-else
                placeholder="Item description"
                v-model="item.description"
                rows="2"
                @keydown="handleKeyDown($event, item)"
              />
            </td>
            <td>
              <div v-if="readonly" class="py-2">{{ item.quantity }}</div>
              <CInput v-else v-model="item.quantity" type="number" min="1" />
            </td>
            <td>
              <div v-if="readonly" class="py-2">{{ item.unit }}</div>
              <CInput v-else v-model="item.unit" aria-label="Unit" />
            </td>
            <td>
              <div v-if="readonly" class="py-2 text-right">RM {{ formatCurrency(parseFloat(item.price) || 0) }}</div>
              <CInput v-else
                v-model="item.price"
                type="number"
                inputmode="decimal"
                min="0"
                step="0.01"
                aria-label="Price"
              />
            </td>
            <td class="font-weight-bold text-right">
              RM {{ formatCurrency(getTotalItemPrice(item)) }}
            </td>
            <td v-if="!readonly">
              <CButtonGroup>
                <CButton
                  color="light"
                  size="sm"
                  @click="$emit('move-item', index, 'up')"
                  :disabled="index === 0"
                  title="Move up"
                  aria-label="Move line up"
                >
                  <CIcon name="cil-arrow-top" />
                </CButton>
                <CButton
                  color="light"
                  size="sm"
                  @click="$emit('move-item', index, 'down')"
                  :disabled="index === items.length - 1"
                  title="Move down"
                  aria-label="Move line down"
                >
                  <CIcon name="cil-arrow-bottom" />
                </CButton>
                <CButton
                  color="light"
                  size="sm"
                  @click="$emit('remove-item', item)"
                  title="Remove"
                  aria-label="Remove line"
                >
                  <CIcon name="cil-trash" />
                </CButton>
              </CButtonGroup>
            </td>
          </tr>
          <tr v-if="items.length === 0">
            <td :colspan="readonly ? 7 : 8" class="text-center py-4">
              <div class="text-muted">
                <CIcon name="cil-basket" size="xl" />
                <p class="mt-2">No items added yet</p>
                <CButton v-if="!readonly" color="primary" size="sm" @click="$emit('add-item')">
                  Add item
                </CButton>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="6" class="text-right font-weight-bold">Subtotal</td>
            <td class="font-weight-bold text-right">RM {{ formatCurrency(grandTotal) }}</td>
            <td v-if="!readonly"></td>
          </tr>
          <tr v-if="showTax">
            <td colspan="4" class="text-right font-weight-bold">Tax</td>
            <td colspan="2">
              <div v-if="readonly" class="py-2">{{ selectedSalesTax ? selectedSalesTax.name : "No tax" }}</div>
              <v-select v-else
                :value="selectedSalesTax"
                :label="'name'"
                :options="salesTaxItems"
                placeholder="Select tax"
                @input="handleSalesTaxSelect"
              />
            </td>
            <td class="font-weight-bold text-right">RM {{ formatCurrency(grandTaxOnly) }}</td>
            <td v-if="!readonly"></td>
          </tr>
          <tr v-if="showTax">
            <td colspan="6" class="text-right font-weight-bold">
              Total (with tax)
            </td>
            <td class="font-weight-bold text-right">
              RM {{ formatCurrency(grandTotalWithTax) }}
            </td>
            <td v-if="!readonly"></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Mobile Card View -->
    <div class="d-md-none">
      <div
        v-for="(item, index) in items"
        :key="item.id"
        class="mb-3 border rounded p-3"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0">Item {{ index + 1 }}</h6>
          <CButtonGroup v-if="!readonly">
            <CButton
              color="light"
              size="sm"
              @click="$emit('move-item', index, 'up')"
              :disabled="index === 0"
              aria-label="Move line up"
            >
              <CIcon name="cil-arrow-top" />
            </CButton>
            <CButton
              color="light"
              size="sm"
              @click="$emit('move-item', index, 'down')"
              :disabled="index === items.length - 1"
              aria-label="Move line down"
            >
              <CIcon name="cil-arrow-bottom" />
            </CButton>
            <CButton
              color="light"
              size="sm"
              @click="$emit('remove-item', item)"
              aria-label="Remove line"
            >
              <CIcon name="cil-trash" />
            </CButton>
          </CButtonGroup>
        </div>

        <div class="form-group">
          <label>Product</label>
          <div v-if="readonly" class="py-2">{{ productName(item) }}</div>
          <div v-else-if="!item.product && item.name" class="small font-weight-bold mb-1">{{ item.name }}</div>
          <v-select
            v-if="!readonly"
            v-model="item.product"
            :label="'name'"
            :options="productItems"
            placeholder="Select product"
            @input="handleProductSelect($event, item)"
          />
        </div>

        <div class="form-group">
          <label>Description</label>

          <!-- Modal Mode: Truncated preview with edit button -->
          <div v-if="useDescriptionModal">
            <div
              class="description-preview border rounded p-2 mb-2"
              @click="openDescriptionModal(item)"
              style="cursor: pointer; min-height: 50px;"
            >
              {{ truncateText(item.description, 80) || 'Click to add a description' }}
            </div>
            <CButton
              size="sm"
              color="light"
              @click="openDescriptionModal(item)"
              block
            >
              <CIcon name="cil-pencil" size="sm" /> Edit description
            </CButton>
          </div>

          <div v-else-if="readonly" class="py-2 description-preview">{{ item.description }}</div>
          <!-- Inline Mode: Direct textarea editing -->
          <CTextarea
            v-else
            placeholder="Item description"
            v-model="item.description"
            rows="2"
            @keydown="handleKeyDown($event, item)"
          />
        </div>

        <CRow>
          <CCol xs="6">
            <div class="form-group">
              <label>Quantity</label>
              <div v-if="readonly" class="py-2">{{ item.quantity }}</div>
              <CInput v-else v-model="item.quantity" type="number" min="1" />
            </div>
          </CCol>
          <CCol xs="6">
            <div class="form-group">
              <label>Unit</label>
              <div v-if="readonly" class="py-2">{{ item.unit }}</div>
              <CInput v-else v-model="item.unit" />
            </div>
          </CCol>
        </CRow>

        <div class="form-group">
          <label>Price (RM)</label>
          <div v-if="readonly" class="py-2">RM {{ formatCurrency(parseFloat(item.price) || 0) }}</div>
          <CInput v-else v-model="item.price" type="number" inputmode="decimal" min="0" step="0.01" />
        </div>

        <div class="d-flex justify-content-between mt-2 pt-2 border-top">
          <div class="text-muted">Total</div>
          <div class="font-weight-bold text-right">
            RM {{ formatCurrency(getTotalItemPrice(item)) }}
          </div>
        </div>
      </div>

      <div
        v-if="items.length === 0"
        class="text-center py-4 border rounded"
      >
        <div class="text-muted">
          <CIcon name="cil-basket" size="xl" />
          <p class="mt-2">No items added yet</p>
          <CButton v-if="!readonly" color="primary" size="sm" @click="$emit('add-item')">
            Add item
          </CButton>
        </div>
      </div>

      <!-- Mobile Summary -->
      <div class="mt-3 p-3 border rounded bg-light">
        <div class="d-flex justify-content-between mb-2">
          <div>Subtotal</div>
          <div class="font-weight-bold text-right">RM {{ formatCurrency(grandTotal) }}</div>
        </div>
        <div v-if="showTax" class="mb-2">
          <div class="form-group">
            <label>Tax</label>
            <div v-if="readonly" class="py-2">{{ selectedSalesTax ? selectedSalesTax.name : "No tax" }}</div>
            <v-select v-else
              :value="selectedSalesTax"
              :label="'name'"
              :options="salesTaxItems"
              placeholder="Select tax"
              @input="handleSalesTaxSelect"
            />
          </div>
          <div class="d-flex justify-content-between mb-2">
            <div>Tax amount</div>
            <div class="font-weight-bold text-right">RM {{ formatCurrency(grandTaxOnly) }}</div>
          </div>
        </div>
        <div class="d-flex justify-content-between">
          <div class="font-weight-bold">
            {{ showTax ? 'Total (with tax)' : 'Grand total' }}
          </div>
          <div class="font-weight-bold text-right">
            RM {{ formatCurrency(showTax ? grandTotalWithTax : grandTotal) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Description Edit Modal -->
    <CModal
      title="Edit item description"
      size="lg"
      :show.sync="descriptionModalShow"
      @update:show="closeDescriptionModal"
    >
      <div v-if="editingItem">
        <p class="font-weight-bold mb-3">
          <CIcon name="cil-notes" /> Item: {{ editingItem.product?.name || 'Untitled' }}
        </p>
        <CTextarea
          label="Description"
          v-model="tempDescription"
          placeholder="Describe the item in detail"
          rows="10"
          ref="descriptionTextarea"
        />
        <small class="text-muted mt-2 d-block">
          Character count: {{ (tempDescription || '').length }}
        </small>
      </div>
      <template #footer>
        <CButton color="light" @click="closeDescriptionModal">
          Cancel
        </CButton>
        <CButton color="primary" @click="saveDescriptionModal">
          Save description
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { formatMyr } from "@/utils/money";

export default {
  name: "BillItemsComponent",
  components: {
    vSelect,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    productItems: {
      type: Array,
      default: () => [],
    },
    grandTotal: {
      type: Number,
      default: 0,
    },
    grandTaxOnly: {
      type: Number,
      default: 0,
    },
    grandTotalWithTax: {
      type: Number,
      default: 0,
    },
    selectedSalesTax: {
      type: Object,
      default: null,
    },
    salesTaxItems: {
      type: Array,
      default: () => [],
    },
    showTax: {
      type: Boolean,
      default: true,
    },
    useDescriptionModal: {
      type: Boolean,
      default: false, // Set to true to enable modal editing mode
    },
    readonly: {
      type: Boolean,
      default: false, // true: lines and tax are shown as text (an approved, posted document)
    },
  },
  data() {
    return {
      descriptionModalShow: false,
      editingItem: null,
      tempDescription: '',
    };
  },
  methods: {
    productName(item) {
      return (item.product && item.product.name) || item.name || "-";
    },

    handleProductSelect(selected, item) {
      this.$emit("product-selected", selected, item);
    },

    handleSalesTaxSelect(selected) {
      if (selected?.id === "add_new") {
        this.$emit("add-new-tax");
      } else {
        this.$emit("tax-selected", selected);
      }
    },

    handleKeyDown(event, item) {
      const start = event.target.selectionStart;
      const end = event.target.selectionEnd;

      if (event.key === "Tab") {
        event.preventDefault();
        item.description =
          item.description.substring(0, start) +
          "    " +
          item.description.substring(end);

        this.$nextTick(() => {
          event.target.selectionStart = event.target.selectionEnd = start + 4;
        });
      }

      if (event.key === "Enter") {
        event.preventDefault();
        item.description =
          item.description.substring(0, start) +
          "\n" +
          item.description.substring(end);

        this.$nextTick(() => {
          event.target.selectionStart = event.target.selectionEnd = start + 1;
        });
      }
    },

    getTotalItemPrice(item) {
      try {
        const quantity = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        return quantity * price;
      } catch (error) {
        return 0;
      }
    },

    // Number part only; the template adds "RM ".
    formatCurrency(amount) {
      return formatMyr(amount);
    },

    // Description Modal Methods
    openDescriptionModal(item) {
      this.editingItem = item;
      this.tempDescription = item.description || '';
      this.descriptionModalShow = true;
      this.$nextTick(() => {
        if (this.$refs.descriptionTextarea) {
          this.$refs.descriptionTextarea.focus();
        }
      });
    },

    closeDescriptionModal() {
      this.descriptionModalShow = false;
      this.editingItem = null;
      this.tempDescription = '';
    },

    saveDescriptionModal() {
      if (this.editingItem) {
        this.$set(this.editingItem, 'description', this.tempDescription);
      }
      this.closeDescriptionModal();
    },

    truncateText(text, length = 50) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
  },
};
</script>

<style scoped>
.table {
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
}

.description-container {
  display: flex;
  flex-direction: column;
}

.description-preview {
  white-space: pre-wrap;
  word-break: break-word;
  transition: background-color 0.2s;
}

.description-preview:empty::before {
  content: 'Click to add a description';
  font-style: italic;
}

@media (max-width: 767.98px) {
  .table-responsive {
    display: none !important;
  }
}
</style>
